import { AxiosError } from 'axios';
import { z } from 'zod';

const DEFAULT_ERROR_MESSAGE = 'An unexpected error occurred';

interface ApiErrorResponse {
  message: string;
  traceId: string;
}

export interface ApiErrorDisplay {
  message: string;
  traceId?: string;
}

export type AxiosApiError = AxiosError<ApiErrorResponse> & {
  response: {
    data: {
      message: string;
      traceId: string;
    };
    status: number;
  };
};

const AxiosApiErrorSchema = z.object({
  response: z.object({
    data: z.object({
      message: z.string(),
      traceId: z.string(),
    }),
    status: z.number(),
  }),
});

const isAxiosApiError = (error: unknown): error is AxiosApiError => AxiosApiErrorSchema.safeParse(error).success;

export const isNetworkError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const axiosError = error as { code?: string; response?: unknown; isAxiosError?: boolean; config?: unknown };

  const networkErrorCodes = ['ECONNABORTED', 'ETIMEDOUT', 'ECONNREFUSED', 'ERR_NETWORK', 'ENOTFOUND', 'ECONNRESET'];
  const hasNetworkErrorCode = axiosError.code !== undefined && networkErrorCodes.includes(axiosError.code);
  const isAxiosErrorWithoutResponse =
    !axiosError.response && (axiosError.isAxiosError === true || axiosError.config !== undefined);

  return hasNetworkErrorCode || isAxiosErrorWithoutResponse;
};

export const getApiError = (
  error?: AxiosApiError | ApiErrorDisplay | Error | null,
  fallbackMessage: string = DEFAULT_ERROR_MESSAGE
): undefined | ApiErrorDisplay => {
  if (!error) {
    return undefined;
  }

  if (isNetworkError(error)) {
    return {
      message: 'Unable to connect to the server. Please try again.',
      traceId: isAxiosApiError(error) ? error.response.data.traceId : undefined,
    };
  }

  if (isAxiosApiError(error) && error.response.status < 500) {
    return {
      message: error.response.data.message,
      traceId: error.response.data.traceId,
    };
  }

  if (isAxiosApiError(error) && error.response.status >= 500) {
    return {
      message: 'The server encountered an error. Please try again in a moment.',
      traceId: error.response.data.traceId,
    };
  }

  return {
    message: fallbackMessage,
    traceId: isAxiosApiError(error) ? error.response.data.traceId : undefined,
  };
};
